import type { ParticipantSearch } from '../../../types/events.ts';
import type { JSX } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from '@fastnorth/kraken/components/layout';
import ExactMatchResults from './ExactMatchResults.tsx';
import OtherMatchResults from './OtherMatchResults.tsx';
import { Text } from '@fastnorth/kraken/components/typography';

type SearchResultsSummaryProps = {
  results: ParticipantSearch;
};

const SearchResultsSummary = ({ results }: SearchResultsSummaryProps): JSX.Element => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'karwa.components.search_results.summary',
  });
  const { exact, other } = results;

  const allResults = [...exact, ...other];
  const hasResults = allResults.length > 0;
  const hasExact = exact.length > 0;
  const hasOnly = other.length > 0;
  const hasBoth = hasExact && hasOnly;

  const message = useMemo(() => {
    let message: 'no_matches' | 'only_possible_matches' | null = null;
    if (!hasResults) {
      message = 'no_matches';
    }
    if (hasOnly && !hasExact) {
      message = 'only_possible_matches';
    }
    if (!message) {
      return null;
    }
    return (
      <Flex spacing={'2 0'} className={'w-full items-center px-6'}>
        <Text>{t(message)}</Text>
      </Flex>
    );
  }, [hasResults, hasOnly, hasExact, t]);

  return (
    <Flex gap={'4'} className={'flex-col'}>
      {message}
      <ExactMatchResults rows={exact} />
      {hasBoth && (
        <Flex className={'w-full justify-center items-center align-middle'} spacing={'4 0'}>
          <Text>{t('possible_matches', { count: other.length })}</Text>
        </Flex>
      )}
      <OtherMatchResults rows={other} />
    </Flex>
  );
};

export default SearchResultsSummary;
